import { supabase } from "@/integrations/supabase/client";

export interface MapboxSuggestion {
  id: string;
  primary: string;
  secondary?: string;
  fullText: string;
  coords: { lat: number; lng: number } | null;
}

let tokenPromise: Promise<string | null> | null = null;

export const getMapboxToken = (): Promise<string | null> => {
  if (!tokenPromise) {
    tokenPromise = (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("get-mapbox-token", { method: "GET" });
        if (error) {
          console.warn("Failed to fetch Mapbox token:", error.message);
          tokenPromise = null;
          return null;
        }
        const token = (data as any)?.token;
        return typeof token === "string" && token.length > 0 ? token : null;
      } catch (err) {
        console.warn("Mapbox token fetch threw:", err);
        tokenPromise = null;
        return null;
      }
    })();
  }
  return tokenPromise;
};

interface AutocompleteOpts {
  types?: string;
  limit?: number;
  country?: string;
}

export const mapboxAutocomplete = async (
  input: string,
  opts: AutocompleteOpts = {}
): Promise<MapboxSuggestion[]> => {
  const token = await getMapboxToken();
  if (!token) return [];

  const params = new URLSearchParams({
    access_token: token,
    autocomplete: "true",
    country: opts.country ?? "GB",
    limit: String(opts.limit ?? 6),
  });
  if (opts.types) params.set("types", opts.types);

  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(input)}.json?${params.toString()}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Mapbox geocoding failed: ${res.status}`);
  const payload = await res.json();

  const features: any[] = Array.isArray(payload?.features) ? payload.features : [];
  return features.map((f) => {
    const fullText: string = f.place_name ?? f.text ?? "";
    const primary: string = f.text ?? fullText;
    const secondary = fullText.startsWith(primary)
      ? fullText.slice(primary.length).replace(/^,\s*/, "")
      : fullText;
    const center = Array.isArray(f.center) ? f.center : null;
    return {
      id: f.id ?? `${primary}-${Math.random()}`,
      primary,
      secondary: secondary || undefined,
      fullText,
      coords: center ? { lng: center[0], lat: center[1] } : null,
    };
  });
};
